/**
 * ProgrammeSlidesCyclomoteur.jsx — Programme officiel Permis AM Cyclomoteur
 * en 3 sessions officielles (Journée 1 · Journée 2 · Sensibilisation).
 *
 * Rendu 100% HTML/CSS (pas d'images externes) mais avec la même hiérarchie
 * visuelle que la version Voiturette : bloc numéro + titre + durée, objectifs
 * pédagogiques, cadre réglementaire (Arrêté 08/11/2012), séquences détaillées.
 * Réservé aux enseignants via TeacherOnlyGate côté page hub.
 */
import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Clock, Target, Bike, Users, ShieldAlert, Wrench, Eye, Route, MessagesSquare, Sparkles, Award } from 'lucide-react';
import { Button } from '../ui/button';

const SLIDES = [
  {
    id: 1,
    num: 1,
    title: 'Théorie & Plateau',
    subtitle: 'Journée 1 — 4 h',
    duree: '4 h',
    color: '#0284c7',
    accent: 'from-sky-600/25 via-sky-500/10 to-transparent',
    icon: Bike,
    resume: 'Découverte du cyclomoteur, sécurité routière et premiers exercices hors circulation sur plateau.',
    objectifs: [
      { icon: Users, text: 'Prendre contact et identifier ses attentes' },
      { icon: ShieldAlert, text: 'Comprendre les enjeux de la sécurité routière' },
      { icon: Bike, text: 'Découvrir le cyclomoteur et ses commandes' },
      { icon: Wrench, text: 'Réaliser les premières manœuvres hors circulation' },
    ],
    sequences: [
      { title: 'Séquence 1 · 30 min · Représentations individuelles', items: ['Auto-évaluation préalable', 'Attentes & représentations', 'Enjeux sécurité routière'] },
      { title: 'Séquence 2 · 2 h · Formation hors circulation', items: ['Installation & équipement', 'Commandes & mise en marche', 'Démarrage, arrêt, freinage', 'Trajectoire & équilibre'] },
      { title: 'Séquence 3 · 1 h 30 · Code de la route', items: ['Signalisation verticale & horizontale', 'Règles de circulation', 'Priorités & intersections'] },
    ],
    objectifReglementaire: 'Acquérir les compétences de maîtrise du cyclomoteur hors circulation.',
  },
  {
    id: 2,
    num: 2,
    title: 'Circulation supervisée',
    subtitle: 'Journée 2 — 3 h',
    duree: '3 h',
    color: '#16a34a',
    accent: 'from-emerald-600/25 via-emerald-500/10 to-transparent',
    icon: Route,
    resume: 'Mise en circulation sur voies ouvertes, application des règles apprises, conduite supervisée par le moniteur.',
    objectifs: [
      { icon: Eye, text: 'Observer, anticiper les situations' },
      { icon: Route, text: 'Adapter son allure et ses trajectoires' },
      { icon: Users, text: 'Partager la route avec les autres usagers' },
      { icon: ShieldAlert, text: 'Gérer les situations à risque' },
    ],
    sequences: [
      { title: 'Séquence 4 · 3 h · Conduite sur voies ouvertes', items: ['Adaptation de l\'allure aux conditions', 'Positionnement sur la chaussée', 'Contrôles visuels & angles morts', 'Intersections & priorités', 'Croisements & dépassements', 'Partage de la route (piétons, cyclistes, VL, PL)', 'Agglomération & hors agglomération'] },
    ],
    objectifReglementaire: 'Mettre en œuvre les connaissances et compétences nécessaires à une conduite sûre sur les voies ouvertes.',
  },
  {
    id: 3,
    num: 3,
    title: 'Sensibilisation aux risques',
    subtitle: 'Séquence 5 — 1 h',
    duree: '1 h',
    color: '#dc2626',
    accent: 'from-rose-600/25 via-rose-500/10 to-transparent',
    icon: MessagesSquare,
    resume: 'Cérémonie de sensibilisation aux risques : vitesse, distracteurs, alcool, comportements — en présence obligatoire d\'un parent si mineur.',
    objectifs: [
      { icon: ShieldAlert, text: 'Prendre conscience des risques réels' },
      { icon: Users, text: 'Comprendre l\'influence des autres (groupe, distracteurs)' },
      { icon: Sparkles, text: 'Adopter un comportement responsable' },
      { icon: Award, text: 'Recevoir son attestation de suivi de formation' },
    ],
    sequences: [
      { title: 'Séquence 5 · 1 h minimum · Sensibilisation aux risques', items: ['Vitesse et distances de sécurité', 'Distracteurs (téléphone, écrans…)', 'Comportements à risque', 'Conséquences des accidents', 'Responsabilité du conducteur'] },
    ],
    encart: {
      label: 'Candidat mineur',
      text: 'Présence obligatoire d\'un parent ou représentant légal pendant la séquence de sensibilisation.',
    },
    objectifReglementaire: 'Remise de l\'attestation de suivi de formation, sous réserve d\'assiduité, participation active et respect des règles.',
  },
];

export default function ProgrammeSlidesCyclomoteur() {
  const [current, setCurrent] = useState(0);
  const slide = SLIDES[current];
  const Icon = slide.icon;

  const prev = () => setCurrent((c) => (c === 0 ? SLIDES.length - 1 : c - 1));
  const next = () => setCurrent((c) => (c === SLIDES.length - 1 ? 0 : c + 1));

  return (
    <section className="mt-10 mb-14" data-testid="programme-slides-cyclomoteur">
      {/* En-tête */}
      <div className="max-w-6xl mx-auto text-center mb-6 px-4">
        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-500/15 border border-amber-500/30 text-amber-300 text-[10px] font-black uppercase tracking-[0.22em] mb-3">
          <Target className="w-3 h-3" /> Programme officiel · 3 sessions · 8 h
        </div>
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-white leading-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
          Le parcours cyclomoteur en 3 étapes
        </h2>
        <p className="mt-2 text-sm sm:text-base text-white/60 max-w-2xl mx-auto">
          Formation officielle sur 2 journées + 1 séquence de sensibilisation.
          Conforme à l'arrêté du 8 novembre 2012 modifié. Support de cours prêt à
          projeter en salle ou à partager aux élèves.
        </p>
      </div>

      {/* Slide courante */}
      <div className="max-w-6xl mx-auto px-4">
        <div
          className={`relative rounded-2xl overflow-hidden border-2 shadow-2xl bg-gradient-to-br ${slide.accent}`}
          style={{ borderColor: `${slide.color}80`, boxShadow: `0 0 0 3px ${slide.color}25, 0 25px 50px -12px ${slide.color}55` }}
          data-testid={`programme-slide-cyclo-${slide.id}`}
        >
          {/* Header slide */}
          <div className="p-5 sm:p-7 border-b border-white/10 flex flex-wrap items-center gap-4" style={{ background: `${slide.color}22` }}>
            <div
              className="w-16 h-16 rounded-2xl flex items-center justify-center text-white font-black text-3xl flex-shrink-0"
              style={{ background: slide.color, boxShadow: `0 8px 20px ${slide.color}66` }}
            >
              {slide.num}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-black uppercase tracking-[0.22em]" style={{ color: slide.color }}>
                {slide.subtitle}
              </div>
              <h3 className="text-2xl sm:text-3xl font-black text-white leading-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                {slide.title}
              </h3>
            </div>
            <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/40 text-white text-sm font-black">
              <Clock className="w-4 h-4" /> {slide.duree}
            </div>
          </div>

          {/* Body : Objectifs + Cadre réglementaire */}
          <div className="grid md:grid-cols-5 gap-0">
            {/* Objectifs pédagogiques */}
            <div className="md:col-span-3 p-5 sm:p-6">
              <div className="text-[10px] font-black uppercase tracking-[0.22em] text-white/50 mb-3">
                Objectifs pédagogiques
              </div>
              <ul className="space-y-2.5">
                {slide.objectifs.map((o, i) => {
                  const OIcon = o.icon;
                  return (
                    <li key={i} className="flex items-start gap-3">
                      <div
                        className="flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center"
                        style={{ background: `${slide.color}30`, border: `1px solid ${slide.color}55` }}
                      >
                        <OIcon className="w-4 h-4" style={{ color: slide.color }} />
                      </div>
                      <span className="text-[13.5px] sm:text-sm text-white/90 leading-snug pt-1.5">{o.text}</span>
                    </li>
                  );
                })}
              </ul>
            </div>

            {/* Cadre réglementaire */}
            <div className="md:col-span-2 p-5 sm:p-6 bg-black/30 border-t md:border-t-0 md:border-l border-white/10">
              <div className="text-[10px] font-black uppercase tracking-[0.22em] text-white mb-1">
                Cadre réglementaire
              </div>
              <p className="text-[11px] text-white/60 leading-snug mb-3 italic">
                Formation AM cyclomoteur · Arrêté du 8 novembre 2012 modifié
              </p>

              {slide.sequences.map((seq, i) => (
                <div key={i} className={i > 0 ? 'mt-3 pt-3 border-t border-white/10' : ''}>
                  <div className="text-[11px] font-black text-white leading-tight mb-1.5">{seq.title}</div>
                  <ul className="space-y-1">
                    {seq.items.map((it, j) => (
                      <li key={j} className="flex gap-1.5 text-[11.5px] text-white/70 leading-snug">
                        <span style={{ color: slide.color }}>•</span> {it}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}

              {slide.encart && (
                <div className="mt-3 p-2.5 rounded-lg bg-amber-500/15 border border-amber-500/40">
                  <div className="text-[10px] font-black uppercase tracking-widest text-amber-300 mb-0.5">
                    ⚠️ {slide.encart.label}
                  </div>
                  <p className="text-[11px] text-white/80 leading-snug">{slide.encart.text}</p>
                </div>
              )}
            </div>
          </div>

          {/* Footer : Objectif réglementaire */}
          <div className="p-4 sm:p-5 border-t border-white/10 flex items-start gap-3" style={{ background: `${slide.color}15` }}>
            <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center" style={{ background: slide.color }}>
              <Target className="w-4 h-4 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-black uppercase tracking-widest mb-0.5" style={{ color: slide.color }}>
                Objectif réglementaire
              </div>
              <p className="text-[13px] text-white/85 leading-snug">{slide.resume}</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="mt-4 flex items-center justify-between gap-3">
          <Button
            onClick={prev}
            variant="outline"
            size="sm"
            className="border-white/20 text-white/85 hover:bg-white/5"
            data-testid="programme-slide-cyclo-prev"
            aria-label="Session précédente"
          >
            <ChevronLeft className="w-4 h-4 mr-1" /> Préc.
          </Button>
          <div className="text-[11px] font-black uppercase tracking-widest text-white/70 tabular-nums px-2">
            {slide.id} / {SLIDES.length}
          </div>
          <Button
            onClick={next}
            size="sm"
            style={{ background: slide.color }}
            className="text-white hover:opacity-90"
            data-testid="programme-slide-cyclo-next"
            aria-label="Session suivante"
          >
            Suiv. <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>

        {/* Mini thumbs */}
        <div className="mt-5 grid grid-cols-3 gap-2.5">
          {SLIDES.map((s, i) => {
            const active = i === current;
            const SIcon = s.icon;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => setCurrent(i)}
                className={`p-3 rounded-xl border-2 text-left transition ${
                  active ? 'opacity-100 scale-[1.02]' : 'opacity-70 hover:opacity-100'
                }`}
                style={{
                  borderColor: active ? s.color : 'rgba(255,255,255,0.1)',
                  background: active ? `${s.color}22` : 'rgba(255,255,255,0.02)',
                  boxShadow: active ? `0 0 0 3px ${s.color}33` : 'none',
                }}
                data-testid={`programme-slide-cyclo-thumb-${s.id}`}
                aria-label={`Aller à la session ${s.id}`}
                aria-pressed={active}
              >
                <div className="flex items-center gap-2 mb-1">
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-black text-sm"
                    style={{ background: s.color }}
                  >
                    {s.num}
                  </div>
                  <div className="inline-flex items-center gap-1 px-1.5 h-5 rounded text-white text-[9px] font-black uppercase tracking-widest" style={{ background: s.color }}>
                    <Clock className="w-2.5 h-2.5" /> {s.duree}
                  </div>
                </div>
                <div className="text-white text-[11.5px] font-black leading-tight">
                  {s.title}
                </div>
                <div className="text-white/55 text-[10px] mt-0.5">{s.subtitle}</div>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}
